import { isCometBrowser, isCometMobileBrowser } from '#pplx/frontend/comet/client/utils.js';
import { getCookie } from '#pplx/frontend/cookies/provider.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { memoize } from 'lodash-es';
/**
 * Cookie name where native client capabilities are stored.
 * Set by native mobile apps to advertise their features.
 */
export const NATIVE_CLIENT_CAPABILITIES_COOKIE = 'native_bridge';
/**
 * Safely parse native client capabilities from JSON string.
 * Validates types to prevent issues with malformed data.
 */
function safeParseNativeClientCapabilities(json) {
    try {
        const parsed = JSON.parse(json);
        const result = {
            info_version: typeof parsed.info_version === 'string' ? parsed.info_version : undefined,
            local_mcp: {
                call: parsed.local_mcp?.call === true,
                edit: parsed.local_mcp?.edit === true,
            },
        };
        return result;
    }
    catch (error) {
        logger.warn('[NativeClientCapabilities] Invalid JSON', error);
        return undefined;
    }
}
/**
 * Get native client capabilities from cookie (raw, without fallback).
 * Memoized with 1-minute cache invalidation.
 */
const getNativeClientCapabilitiesRaw = memoize(function getNativeClientCapabilitiesRaw() {
    const str = getCookie(NATIVE_CLIENT_CAPABILITIES_COOKIE);
    if (!str) {
        return undefined;
    }
    return safeParseNativeClientCapabilities(str);
}, () => Math.floor(Date.now() / (3600 * 1e3)));
/**
 * Check if the native capabilities cookie is set.
 * Returns true if the cookie exists, regardless of whether it contains valid data.
 */
export function isNativeCapabilityCookieSet() {
    return Boolean(getCookie(NATIVE_CLIENT_CAPABILITIES_COOKIE));
}
/**
 * Get native client capabilities with fallback logic.
 *
 * Priority order:
 * 1. Cookie value (set by native mobile apps)
 * 2. Comet desktop: Full capabilities (local_mcp.call=true, edit=true)
 * 3. Comet mobile: No capabilities (undefined) - must be explicitly set via cookie
 * 4. Regular browser: No capabilities (undefined)
 *
 * Memoized with 1-minute cache invalidation.
 *
 * Note: When adding new capabilities, also update the Comet desktop fallback below.
 */
export const getNativeClientCapabilities = memoize(function getNativeClientCapabilities() {
    const info = getNativeClientCapabilitiesRaw();
    // Always prefer existing info from cookie
    if (info) {
        return info;
    }
    // For Comet Mobile - report NO capabilities
    // Mobile must explicitly set capabilities via cookie
    if (isCometMobileBrowser()) {
        return undefined;
    }
    // For Comet desktop - report all capabilities as fallback
    if (isCometBrowser()) {
        return {
            info_version: '1',
            local_mcp: {
                call: true,
                edit: true,
            },
        };
    }
    return undefined;
}, () => Math.floor(Date.now() / (3600 * 1e3)));
//# sourceMappingURL=nativeClientCapabilities.js.map